<template>
  <section
    v-if="visible"
    class="desktop-update"
    :class="{ 'desktop-update--compact': compact }"
    aria-label="客户端更新"
  >
    <div class="desktop-update__copy" role="status">
      <strong
        ><i class="ri-refresh-line" aria-hidden="true" />客户端更新<span v-if="state">
          · v{{ state.currentVersion }}</span
        ></strong
      >
      <p>{{ description }}</p>
      <a-progress
        v-if="state?.phase === 'downloading'"
        :percent="Math.floor(state.percent)"
        size="small"
        aria-label="更新下载进度"
      />
      <p v-if="store.updateError || state?.message" class="desktop-update__message">
        {{ store.updateError || state?.message }}
      </p>
    </div>
    <a-space wrap>
      <a-button
        v-if="state?.supported && !compact"
        :disabled="busy || state.phase === 'downloaded'"
        :loading="state.phase === 'checking'"
        @click="store.updateClient('check')"
        >检查更新</a-button
      >
      <a-button
        v-if="
          state?.supported && state.latestVersion && ['available', 'error'].includes(state.phase)
        "
        type="primary"
        @click="store.updateClient('download')"
        >{{ state.phase === 'error' ? '重试下载' : '下载更新' }}</a-button
      >
      <a-button
        v-if="state?.phase === 'downloaded' || state?.phase === 'installing'"
        type="primary"
        :loading="state.phase === 'installing'"
        @click="store.updateClient('install')"
        >重启更新</a-button
      >
      <a-button v-if="compact" @click="dismissedVersion = state?.latestVersion ?? ''"
        >稍后</a-button
      >
      <a-button v-if="store.updateError && !state" @click="store.updateClient('status')"
        >重试</a-button
      >
    </a-space>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useDesktopStore } from '@/stores/desktop.store';
const props = defineProps<{ compact?: boolean }>();
const store = useDesktopStore();
const state = computed(() => store.updateState);
const dismissedVersion = ref('');
const visible = computed(() =>
  props.compact
    ? state.value?.phase === 'downloaded' && state.value.latestVersion !== dismissedVersion.value
    : true,
);
const busy = computed(
  () => state.value && ['checking', 'downloading', 'installing'].includes(state.value.phase),
);
const description = computed(() => {
  const value = state.value;
  if (!value) return '正在获取客户端更新信息。';
  if (!value.supported) return '此版本不支持自动更新。';
  switch (value.phase) {
    case 'checking':
      return '正在检查新版本…';
    case 'available':
      return `发现新版本 v${value.latestVersion}。`;
    case 'downloading':
      return `正在后台下载 v${value.latestVersion}，可以继续使用客户端。`;
    case 'downloaded':
      return `v${value.latestVersion} 已下载，重启后生效。请先保存未提交的内容。`;
    case 'installing':
      return '正在准备安装更新…';
    case 'error':
      return '更新未完成，可以重试。';
    default:
      return '启动及使用期间自动检查并下载新版，安装前由你确认重启。';
  }
});
</script>

<style scoped>
.desktop-update {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px;
  margin-top: 16px;
  border: 1px solid var(--aw-border);
  border-radius: var(--aw-radius-lg);
  background: var(--aw-surface);
}
.desktop-update__copy {
  flex: 1;
  min-width: 220px;
}
.desktop-update strong {
  display: flex;
  align-items: center;
  gap: 6px;
}
.desktop-update p {
  margin: 6px 0 0;
  color: var(--aw-text-secondary);
  overflow-wrap: anywhere;
}
.desktop-update .desktop-update__message {
  color: var(--aw-warning);
}
.desktop-update--compact {
  margin: 0;
  border-radius: 0;
}
</style>
